import BasePortableText, {
  BlockContentSerializers
} from "@sanity/block-content-to-react";
import React from "react";
import config from "../../config/siteConfig";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../../shared/string";
import BackgroundBlockPattern from "../BackgroundBlockPattern";
import { BannerFinancialInformation, BannerTextAndImage } from "../Banner";
import { CardsGrid } from "../CardsGrid";
import CourseInfoBlock from "../Courses/CourseInfoBlock/CourseInfoBlock";
import { DisclaimerBanner } from "../DisclaimerBanner";
import Dropdown from "../Dropdown/Dropdown";
import { DualLinkBlock } from "../DualLinkBlock";
import FactList from "../FactList";
import FaqList from "../FaqList";
import FormSection, { FormSectionProps } from "../FormSection";
import { FormSelector } from "../FormSelector";
import { HelpCardPair, HelpSingleCard } from "../HelpCard";
import { HouseHealthForm } from "../HouseHealthForm";
import IconCardBlock from "../IconCardBlock";
import IconTextBlock from "../IconTextBlock";
import ImageWithText from "../ImageWithText/ImageWithText";
import { InvestorRelations } from "../InvestorRelations";
import LatestNews from "../LatestNews";
import LinkColumnList from "../LinkColumnList";
import { MarketSelectorPortableText } from "../MarketSelectorPortableText";
import NearbyOfficeList from "../Office/NearbyOfficeList";
import PestList from "../PestList";
import Reviews from "../Reviews/ReviewsBlock";
import { ReviewsBlock } from "../ReviewsBlock";
import SanityImage from "../SanityImage";
import SimpleButton from "../SimpleButton";
import Video from "../Video/VideoBlock";
import WhatsYourSituation from "../WhatsYourSituation";
import BlockRenderer from "./BlockRenderer";
import CustomHtmlRenderer from "./CustomHtmlRenderer";
import LinkRenderer from "./LinkRenderer";
import ListItemRenderer from "./ListItemRenderer";
import ListRenderer from "./ListRenderer";
import PortableTextImage from "./PortableTextImage";
import PortableTextTable from "./PortableTextTable";
import PortableTextWithBackground from "./PortableTextWithBackground";
import { RelatedCardsSerializer } from "./serializers/RelatedCardsSerializer";

export enum PortableTextColorTheme {
  None,
  Neutral,
  NeutralDark,
  PrimaryDark,
  PrimaryLight
}

export type PortableTextColorThemeProps = {
  elementClasses?: ElementClasses;
  defaultTextColor?: string;
  wrapperClasses?: string;
  primaryButtonColor?: string;
  secondaryButtonColor?: string;
};

export type GetSerializersProps = {
  additionalBlockProps?: Record<string, object>;
  colorTheme: PortableTextColorTheme;
  elementClasses?: ElementClasses;
};

export const PortableTextColorThemes: Record<
  PortableTextColorTheme,
  PortableTextColorThemeProps | undefined
> = {
  [PortableTextColorTheme.None]: {
    elementClasses: {
      p: "text-current",
      h2: "text-current mt-20",
      h3: "text-current mt-20",
      h4: "text-current mt-20",
      h5: "text-current mt-20",
      h6: "text-current mt-20"
    }
  },
  [PortableTextColorTheme.Neutral]: {
    defaultTextColor: "brand-primary-20",
    elementClasses: {
      h3Subheader:
        "text-brand-primary-dim text-base uppercase font-bold lg:text-xl lg:mt-2",
      p: "text-paragraph-primary"
    }
  },
  [PortableTextColorTheme.NeutralDark]: {
    defaultTextColor: "brand-primary-20",
    wrapperClasses:
      "bg-brand-primary-90 max-w-none px-0 py-0 mb-12 relative overflow-hidden md:mb-32 lg:py-12 lg:slice-both--long-section lg:z-1",
    elementClasses: {
      h3: "text-brand-primary-30 mt-20 text-xl lg:text-3.5xl",
      h3Subheader:
        "text-brand-primary-dim text-base uppercase font-bold lg:text-xl lg:mt-2"
    }
  },
  [PortableTextColorTheme.PrimaryDark]: {
    defaultTextColor: "neutral-100",
    wrapperClasses:
      "bg-brand-primary-30 max-w-none px-0 py-12 lg:py-48 relative overflow-hidden lg:slice-both--long-section mb-12 md:mb-32",
    elementClasses: {
      h3Subheader:
        "text-brand-primary-80 text-base uppercase font-bold lg:text-xl lg:mt-2"
    },
    primaryButtonColor: "primary-light"
  },
  [PortableTextColorTheme.PrimaryLight]: {
    defaultTextColor: "paragraph-highlighted",
    wrapperClasses:
      "max-w-4xl mx-auto py-12 bg-brand-primary-100 border border-paragraph-border rounded-lg mb-12 md:mb-32",
    elementClasses: {
      h3: "text-brand-primary-30 mt-20 text-xl lg:text-3.5xl",
      h3Subheader:
        "text-brand-primary-dim text-base uppercase font-bold lg:text-xl lg:mt-2"
    }
  }
};

export type ElementClasses = {
  a?: string;
  h1?: string;
  h2?: string;
  h3?: string;
  h4?: string;
  h5?: string;
  h6?: string;
  h3Subheader?: string;
  p?: string;
  ul?: string;
  li?: string;
  default?: string;
};

export type PortableTextBlock = {
  _type: string;
  _key: string;
  children: {
    _type: string;
    text: string;
  }[];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type PortableTextBody = PortableTextBlock[];

export interface PortableTextProps {
  _rawBody: PortableTextBody | undefined;
  additionalBlockProps?: Record<string, object>;
  addSections?: boolean;
  colorTheme?: PortableTextColorTheme;
  elementClasses?: ElementClasses;
  hasBackground?: boolean;
  wide?: boolean;
}

type BlockWithMarkDefs = PortableTextBlock & {
  markDefs?: unknown[];
};

/**
 * In some odd scenarios when applying bold in a text in Sanity,
 * the markDefs array is not created, but the @sanity/block-content-to-react plugin
 * expects it to be there.
 * Hacky function to fix this.
 */
const fixMarkDefs = (block: BlockWithMarkDefs) => {
  if (block._type === "block" && block.markDefs === undefined) {
    block.markDefs = [];
  }
};

export const getPortableTextColorTheme = (
  theme: PortableTextColorTheme,
  customElementClasses?: ElementClasses
): PortableTextColorThemeProps => {
  const themeProperties: PortableTextColorThemeProps = {
    ...PortableTextColorThemes[PortableTextColorTheme.None],
    ...PortableTextColorThemes[theme]
  } as PortableTextColorThemeProps;

  themeProperties.elementClasses = {
    ...PortableTextColorThemes[PortableTextColorTheme.None]?.elementClasses,
    ...themeProperties.elementClasses
  };

  if (customElementClasses) {
    themeProperties.elementClasses = {
      ...themeProperties.elementClasses,
      ...customElementClasses
    };
  }

  return themeProperties;
};

export function getSerializers({
  additionalBlockProps,
  colorTheme,
  elementClasses: customElementClasses
}: GetSerializersProps): BlockContentSerializers {
  const themeProps = getPortableTextColorTheme(colorTheme, customElementClasses);

  return {
    list: props => (
      <ListRenderer
        sanityProps={props}
        customElementClasses={themeProps?.elementClasses}
      />
    ),
    listItem: props => (
      <ListItemRenderer
        sanityProps={props}
        customElementClasses={themeProps?.elementClasses}
      />
    ),
    marks: {
      highlight: props => (
        <span className="text-paragraph-highlighted font-bold">{props.children}</span>
      ),
      inlineLink: props => (
        <LinkRenderer className={customElementClasses?.a} sanityProps={props} />
      )
    },
    types: {
      backgroundPattern: props => (
        <div className="relative">
          <BackgroundBlockPattern {...props.node} />
        </div>
      ),
      banner: props => <BannerTextAndImage {...props.node} />,
      bannerFinancialInformation: props => <BannerFinancialInformation {...props.node} />,
      block: props => (
        <BlockRenderer
          sanityProps={props}
          customElementClasses={themeProps?.elementClasses}
        />
      ),
      cardPair: props => (
        <HelpCardPair {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      card: props => (
        <HelpSingleCard {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      disclaimerBanner: props => <DisclaimerBanner {...props.node} />,
      dualLinkBlock: props => <DualLinkBlock {...props.node} />,
      factList: props => (
        <FactList
          {...props.node}
          portableTextColorTheme={colorTheme}
          portableTextCustomElementClasses={customElementClasses}
        />
      ),
      faqList: props => <FaqList {...props.node} portableTextColorTheme={colorTheme} />,
      formBlock: props => <FormSection {...(props.node as FormSectionProps)} />,
      houseHealthCheckForm: props => <HouseHealthForm {...props.node} />,
      iconCardBlock: props => (
        <IconCardBlock {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      iconTextBlock: props => (
        <IconTextBlock {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      // NOTE: this block shouldn't be used in portable text anymore,
      // use SanityImage to get it working for "old" pages
      imageExtended: props => (
        <div className="flex justify-center max-w-none relative mb-14 px-6">
          <SanityImage
            image={props.node as never}
            // limit max width
            width={920}
          />
        </div>
      ),
      imageWithText: props => (
        <ImageWithText {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      marketSelector: props => <MarketSelectorPortableText {...props.node} />,
      portableTextImage: props => <PortableTextImage {...props.node} />,
      portableTextWithBackground: props => (
        <PortableTextWithBackground
          _rawBody={props.node.portableTextContent as PortableTextBody}
          colorTheme={PortableTextColorTheme.PrimaryLight}
        />
      ),
      // Keep "freeTextWithBackground" as fallback as the original schema type name has not been changed
      freeTextWithBackground: props => (
        <PortableTextWithBackground
          _rawBody={props.node.portableTextContent as PortableTextBody}
          colorTheme={PortableTextColorTheme.PrimaryLight}
        />
      ),
      latestNews: props => (
        <LatestNews {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      linkColumnList: props => <LinkColumnList {...props.node} />,
      investorRelations: props => <InvestorRelations {...props.node} />,
      pestList: props => (
        <PestList {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      nearbyOfficesList: props => (
        <NearbyOfficeList {...props.node} portableTextColorThemeProps={themeProps} />
      ),
      relatedLocatorCards: props => (
        <RelatedCardsSerializer
          {...props.node}
          {...additionalBlockProps?.relatedLocatorCards}
        />
      ),
      script: props => (
        <CustomHtmlRenderer
          {...props.node}
          wrapperClassNames="w-full max-w-screen-xl mx-auto mb-12 px-4 md:px-6 lg:mb-32"
        />
      ),
      table: props => (
        <PortableTextTable {...props.node} portableTextColorTheme={colorTheme} />
      ),
      whatsYourSituation: props => <WhatsYourSituation {...props.node} />,
      cardsGrid: props => <CardsGrid {...props.node} />,
      // Google Review Block (Will be moved to reviews)
      reviewBlock: props => {
        const placeId =
          props.node?.reviewBlockOffice?.reviewPlace?.googleBusinessAccountId;

        return <ReviewsBlock {...props.node} placeId={placeId} />;
      },
      courseInfoBlock: props => <CourseInfoBlock {...props.node} />,
      dropdown: props => (
        <Dropdown {...props.node}>
          <PortableText
            _rawBody={props?.node?.portableTextContent as PortableTextBody | undefined}
            colorTheme={PortableTextColorTheme.Neutral}
          />
        </Dropdown>
      ),
      reviews: props => <Reviews {...props.node} />,
      video: props => <Video {...props.node} />,
      simpleButton: props => <SimpleButton {...props.node} />,
      formSelector: props => <FormSelector {...props.node} />
    }
  };
}

const getPortableTextBlockElement = (
  addSections: boolean,
  blocks: PortableTextBody,
  hasBackground: boolean,
  serializers: BlockContentSerializers,
  wide: boolean
) => {
  let portableTextElement: JSX.Element;

  try {
    portableTextElement = (
      <BasePortableText
        blocks={blocks}
        className={"anticimex-portable-text"}
        dataset={config.sanityDataset}
        key={blocks[0]._key}
        projectId={config.sanityProjectId}
        serializers={serializers}
      />
    );
  } catch (e) {
    // Don't let unknown block types completely fail the gatsby build, just ignore them
    if (e?.message?.startsWith("Unknown block type")) {
      console.warn(e);
      return <></>;
    } else {
      throw e;
    }
  }

  // Only add the class "anticimex-portable-text" to the div wrapper if the blocks length
  // is 1, since BasePortableText adds its own div wrapper if blocks length > 1.
  if (addSections) {
    let sectionClassNames = "relative w-full mx-auto mb-12 px-4 md:px-6 lg:mb-32";

    if (wide) {
      sectionClassNames += " max-w-screen-xl";
    } else {
      sectionClassNames += " max-w-3xl";
    }

    if (hasBackground) {
      sectionClassNames += " last:mb-0";
    }

    if (blocks.length === 1) {
      sectionClassNames += " anticimex-portable-text";
    }

    return (
      <div key={blocks[0]._key} className={sectionClassNames}>
        {portableTextElement}
      </div>
    );
  } else {
    if (blocks.length === 1) {
      return (
        <div key={blocks[0]._key} className="anticimex-portable-text">
          {portableTextElement}
        </div>
      );
    } else {
      return portableTextElement;
    }
  }
};

function groupPortableTextAndBlocks({
  _rawBody,
  additionalBlockProps,
  addSections = true,
  colorTheme = PortableTextColorTheme.None,
  elementClasses,
  hasBackground,
  wide = false
}: PortableTextProps) {
  if (_rawBody) {
    const content: JSX.Element[] = [];
    let blocks: PortableTextBody = [];

    // TODO: Memoize getSerializers()?
    const serializers = getSerializers({
      additionalBlockProps,
      colorTheme,
      elementClasses
    });

    for (const block of _rawBody) {
      fixMarkDefs(block);

      // Gather continuous rich text into one section
      if (
        block._type === "block" ||
        block._type === "image" ||
        block._type === "simpleButton"
      ) {
        blocks.push(block);
      } else {
        if (blocks.length > 0) {
          // Don't add sections for empty blocks (containing only whitespace)
          if (
            blocks.some(
              block =>
                block._type === "image" ||
                block._type === "simpleButton" ||
                block.children?.some(child => !isNullOrWhiteSpace(child.text))
            )
          ) {
            content.push(
              getPortableTextBlockElement(
                addSections,
                blocks,
                hasBackground ?? false,
                serializers,
                wide
              )
            );
          }

          blocks = [];
        }

        // Objects have their own sections
        content.push(
          <BasePortableText
            key={block._key}
            blocks={block}
            projectId={config.sanityProjectId}
            dataset={config.sanityDataset}
            serializers={serializers}
          />
        );
      }
    }

    // Add any remaining rich text
    if (
      blocks.length > 0 &&
      blocks.some(
        block =>
          block._type === "image" ||
          block._type === "simpleButton" ||
          block.children?.some(child => !isNullOrWhiteSpace(child.text))
      )
    ) {
      content.push(
        getPortableTextBlockElement(
          addSections,
          blocks,
          hasBackground ?? false,
          serializers,
          wide
        )
      );
    }

    return content;
  }
}

/**
 * Iterate through the blocks in PortableTextBody and replace shy with its unicode equivalent in children of type "span".
 * @param _rawBody PortableTextBody to process
 * @returns the processed PortableTextBody
 */
function replaceShyInSpanText(_rawBody: PortableTextBody): PortableTextBody {
  if (_rawBody) {
    for (const block of _rawBody) {
      if (block._type === "block") {
        for (const child of block.children) {
          if (child._type === "span" && !isNullOrWhiteSpace(child.text)) {
            child.text = replaceWithUnicode(child.text) ?? "";
          }
        }
      }
    }
  }
  return _rawBody;
}

const PortableText = (props: PortableTextProps) => {
  const { colorTheme = PortableTextColorTheme.None } = props;

  const sanitizedBody = props._rawBody ? replaceShyInSpanText(props._rawBody) : undefined;

  if (colorTheme !== PortableTextColorTheme.None) {
    const { defaultTextColor } = getPortableTextColorTheme(
      colorTheme ?? PortableTextColorTheme.Neutral
    );

    return (
      <div className={`text-${defaultTextColor}`}>
        {groupPortableTextAndBlocks({ ...props, _rawBody: sanitizedBody })}
      </div>
    );
  } else {
    return <>{groupPortableTextAndBlocks({ ...props, _rawBody: sanitizedBody })}</>;
  }
};

export default PortableText;
